import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { SortDirectionEnum } from "../../utils/queryApi/queryApiRequest"
import type { FiltersObject } from "./types"
import { Expression, T } from "../../utils/queryApi/expressions"
import type { FiltersState } from "./reducer"
import { GLOBAL_FILTER_IDENTIFIER } from "./reducer"
import { StatusFilterEnum } from "../common/filters/StatusFilterComponent"

export const composeSearchAmountExpression = (amountFieldname: string, amount: string): any => {
    // If does not include $ or has any unwanted characters (except .$-)
    if (amount.match(/[^\d.$-]/g)) {
        return T(amountFieldname).eq(-9999999999) // Todo: discuss with František if we can introduce "empty" expression
    }

    // Remove any non-numeric characters (except .)
    const apiAmount = amount.replace(/[^\d.]/g, "")

    // If does not include point
    if (!apiAmount.includes(".")) {
        const fromNumber = parseInt((parseFloat(apiAmount) * 100).toFixed(2), 10)
        const toNumber = parseInt(((parseFloat(apiAmount) + 1) * 100).toFixed(2), 10)
        return T(amountFieldname)
            .ge(fromNumber)
            .and(T(amountFieldname).lt(toNumber))
            .or(T(amountFieldname).le(-fromNumber).and(T(amountFieldname).gt(-toNumber)))
    } // If includes one number after point
    else if (apiAmount.split(".", 2)[1].length === 1) {
        const fromNumber = parseInt((parseFloat(apiAmount) * 100).toFixed(2), 10)
        const toNumber = parseInt(((parseFloat(apiAmount) + 0.1) * 100).toFixed(2), 10)
        return T(amountFieldname)
            .ge(fromNumber)
            .and(T(amountFieldname).lt(toNumber))
            .or(T(amountFieldname).le(-fromNumber).and(T(amountFieldname).gt(-toNumber)))
    } // If includes two or more numbers after point
    else {
        const equalsTo = parseInt((parseFloat(apiAmount) * 100).toFixed(2), 10)
        return T(amountFieldname).eq(equalsTo).or(T(amountFieldname).eq(-equalsTo))
    }
}

export const getAntSortDirection = (dir: Maybe<string>) => {
    if (!dir) {
        return null
    }

    return dir === SortDirectionEnum.Asc ? "ascend" : "descend"
}

export const getApiSortDirection = (dir: Maybe<string>) => {
    if (!dir) {
        return null
    }

    return dir === "ascend" ? SortDirectionEnum.Asc : SortDirectionEnum.Desc
}

export const transformAntTableFilter = (filter: any, sorter: any): FiltersObject => {
    return {
        sort: {
            by: sorter.columnKey || "",
            direction: getApiSortDirection(sorter.order),
        },
        ...filter,
    }
}

export const composeSearchExpressions = (fields: string[], searchValue: string) =>
    fields.slice(1).reduce(
        (acc: Expression, fieldName: string) => acc.or(T(fieldName).ct(searchValue)),
        T(fields[0]).ct(searchValue) // Initial value
    )

export const DEFAULT_TABLE_PAGE_LIMIT = 100

export const createDefaultPaginationFilter = () => ({
    currentPage: 1,
    pageLimit: DEFAULT_TABLE_PAGE_LIMIT,
    totalCount: 0,
})

export const defaultFilter = {
    pagination: createDefaultPaginationFilter(),
    sort: {
        by: null,
        direction: null,
    },
}
// Global filter

export const createGlobalDefaultFilter = (
    state: FiltersState,
    locationsIds: string[],
    from: string,
    to: string,
    fromDate: string,
    toDate: string
) => {
    return {
        ...state,
        filters: {
            ...state.filters,
            [GLOBAL_FILTER_IDENTIFIER]: {
                ...state.filters.global,
                selectedMibTerminalId: null,
                location_name: locationsIds,
                ...(state.filters.global.timestamp === null
                    ? {
                          timestamp: {
                              from: from,
                              to: to,
                          },
                      }
                    : null),
                ...(state.filters.global.date === null
                    ? {
                          date: {
                              from: fromDate,
                              to: toDate,
                          },
                      }
                    : null),
            },
        },
    }
}
// Customers report

export const CUSTOMERS_REPORT_SEARCH_FIELDS = [
    "customer_name",
    "customer_personal_name",
    "gender",
    "position",
    "phone_number",
    "email_address",
    "street_line1",
    "street_line2",
    "city",
    "state",
    "zip",
]

export const createCustomersReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        customers: {
            ...state.filters.customers,
            sort: {
                by: "total_spend",
                direction: "Desc",
            },
            search: {
                value: "",
                fields: CUSTOMERS_REPORT_SEARCH_FIELDS,
            },
            pagination: createDefaultPaginationFilter(),
        },
    },
})
// Customers feedback report

export const CUSTOMERS_FEEDBACK_REPORT_SEARCH_FIELDS = ["customer_name", "feedback"]

export const createCustomersFeedbackReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        customersFeedback: {
            ...state.filters.customersFeedback,
            sort: {
                by: "origin_timestamp",
                direction: "Desc",
            },
            search: {
                value: "",
                fields: CUSTOMERS_FEEDBACK_REPORT_SEARCH_FIELDS,
            },
            pagination: createDefaultPaginationFilter(),
        },
    },
})

export const createTimesheetReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        timesheet: {
            ...state.filters.timesheet,
            sort: {
                by: "clock_in_timestamp",
                direction: "asc",
            },
            search: {
                value: "",
                fields: [],
            },
            pagination: createDefaultPaginationFilter(),
        },
    },
})
// Transactions report

export const TRANSACTIONS_REPORT_SEARCH_FIELDS = [
    "transaction_number",
    "order_number",
    "transactor_name",
    "assignee_name",
    "location_name",
    "terminal_name",
    "operation",
    "result",
    "payment_type",
    "result_message",
    "payment_subtype",
    "external_payment_type",
    "approval_code",
]

export const createTransactionsReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        transactionsReport: {
            ...state.filters.transactionsReport,
            sort: {
                by: "timestamp",
                direction: "Desc",
            },
            search: {
                value: "",
                fields: TRANSACTIONS_REPORT_SEARCH_FIELDS,
            },
            pagination: createDefaultPaginationFilter(),
        },
    },
})
// Orders report

export const ORDERS_REPORT_SEARCH_FIELDS = [
    "order_number",
    "status",
    "assignee_name",
    "assignment_name",
    "location_id",
    "location_name",
    "terminal_name",
    "customer_name",
    "payment_types",
    "payment_status_code",
    "card_names",
]

export const createOrdersReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        orders: {
            sort: {
                by: "created_timestamp",
                direction: "Desc",
            },
            search: {
                value: "",
                fields: ORDERS_REPORT_SEARCH_FIELDS,
            },
            status: StatusFilterEnum.Active,
            source: "all",
            pagination: createDefaultPaginationFilter(),
            ...state.filters.orders,
        },
    },
})
// Customers report

export const CASH_REPORT_SEARCH_FIELDS = [
    "location_id",
    "transaction_type",
    "category_name",
    "pay_in_pay_out",
    "transaction_number",
    "location_name",
    "assignee_name",
    "note",
    "account_name",
]

export const createCashReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        cash: {
            ...state.filters.cash,
            sort: {
                by: "origin_timestamp",
                direction: "Desc",
            },
            search: {
                value: "",
                fields: CASH_REPORT_SEARCH_FIELDS,
            },
            pagination: createDefaultPaginationFilter(),
        },
    },
})
// Summary report

export const createSummaryReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: { ...state.filters, summaryReport: { ...state.filters.summaryReport } },
})

export const createSalesByItemsReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        salesByItems: {
            ...state.filters.salesByItems,
            sort: {
                by: "origin_timestamp",
                direction: "Desc",
            },
            view: ["categories", "items"],
            collapsible: null,
        },
    },
})

export const createSpecialPricingsLogReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        specialPricingLog: {
            ...state.filters.specialPricingLog,
            sort: {
                by: "origin_timestamp",
                direction: "Desc",
            },
        },
    },
})

export const createVoidsLogReportDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: {
        ...state.filters,
        voidsLog: {
            ...state.filters.voidsLog,
            sort: {
                by: "origin_timestamp",
                direction: "Desc",
            },
        },
    },
})
// Employees

export const createEmployeesDefaultFilter = (state: FiltersState) => ({
    ...state,
    filters: { ...state.filters, employees: { ...state.filters.employees, status: StatusFilterEnum.Active } },
})
